import { parseISO, format, intlFormat } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import useFilms from '../../hooks/useFilms';

import { useSite } from '../../contexts/SiteContext';
import Blocks from '../blocks/Blocks';
import WeekHeader from '../icons/WeekHeader';
import EventsByDate from '../partials/EventsByDate';
import FloatingCta from '../partials/FloatingCta';
import PageMeta from '../partials/PageMeta';
import Projectron from '../partials/Projectron';

import styles from '../../../styles/pages/week.module.css';

const propTypes = { page: PropTypes.shape({ id: PropTypes.string }) };

function WeekPage({ page }) {
    const { url } = useSite();
    const { blocks = null, startDate = null, endDate = null } = page || {};
    const start = startDate !== null ? parseISO(startDate) : null;
    const end = endDate !== null ? parseISO(endDate) : null;

    const { startDay, startMonth, startYear, endDay, endMonth, endYear } = useMemo(
        () => ({
            startDay: start !== null ? format(start, 'd') : null,
            startMonth: start !== null ? intlFormat(start, { month: 'long' }) : null,
            startYear: start !== null ? format(start, 'yyyy') : null,
            endDay: end !== null ? format(end, 'd') : null,
            endMonth: end !== null ? intlFormat(end, { month: 'long' }) : null,
            endYear: end !== null ? format(end, 'yyyy') : null,
        }),
        [start, end],
    );

    const { data: films = null } = useFilms({ query: { shuffle: true }, count: 12 });

    const headerBlocks = useMemo(() => {
        const items = (blocks || []).filter((block) => block.type !== 'social');
        return items.length > 0 ? items : null;
    }, [blocks]);

    const footerBlocks = useMemo(() => {
        const items = (blocks || []).filter((block) => block.type === 'social');
        return items.length > 0 ? items : null;
    }, [blocks]);

    return (
        <div className={styles.container}>
            <PageMeta page={page} withoutTitleSuffix />
            <div className={styles.top}>
                <a href={url} target="_self" rel="noopener noreferrer" className={styles.header}>
                    <WeekHeader className={styles.logo} />
                    <p>
                        {startDay} {startMonth === endMonth ? '' : startMonth} &rarr; {endDay}{' '}
                        {endMonth}
                    </p>
                    <p>{startYear === endYear ? startYear : `${startYear} — ${endYear}`}</p>
                </a>

                <Projectron className={styles.projectron} films={films} />
            </div>
            {headerBlocks !== null && headerBlocks.length > 0 ? (
                <Blocks
                    className={styles.blocks}
                    blockClassName={styles.block}
                    blocks={headerBlocks}
                    // padded={false}
                    isContainer
                />
            ) : null}
            <EventsByDate className={styles.events} query={{ in_doc_week: true }} />
            {footerBlocks !== null && footerBlocks.length > 0 ? (
                <Blocks
                    className={styles.footerBlocks}
                    blockClassName={styles.block}
                    blocks={footerBlocks}
                    padded={false}
                />
            ) : null}
            <div className={styles.bottom}>
                <FloatingCta className={styles.cta} />
            </div>
        </div>
    );
}

WeekPage.propTypes = propTypes;

export default WeekPage;
